import { safeArray } from './format';

/**
 * One view over the places data regardless of document shape.
 *
 * Property-context 2.2.0 splits the `nearby_places` umbrella into three optional
 * top-level datasets — `commercial_anchors` ({ anchors }), `public_places`
 * ({ places }) and `adverse_uses` ({ facilities }) — each with its own
 * `section_status`. `nearby_places` stays in the response, deprecated and
 * unchanged, and still owns `general_amenities` / `data_quality`.
 *
 * Per layer: a split dataset that was SENT wins (an explicit `null` means the
 * backend excluded it, so nothing is shown and the legacy copy is NOT resurrected);
 * a split dataset that is ABSENT (older backend) falls back to the matching
 * `nearby_places` fields. Components read only through this selector.
 */
export const PLACES_DATASET_KEYS = ['commercial_anchors', 'public_places', 'adverse_uses'];

const sent = (v) => v !== undefined;

function pickLayer(result, legacy, key, listField, legacyListField, legacySummaryField) {
  const dataset = result[key];
  if (sent(dataset)) {
    if (!dataset) return { source: key, list: null, summary: {}, scope: null, notes: [], excluded: true };
    return { source: key, list: safeArray(dataset[listField]), summary: dataset.summary || {}, scope: dataset.scope || null, notes: safeArray(dataset.notes), excluded: false };
  }
  if (legacy && sent(legacy[legacyListField])) {
    return { source: 'nearby_places', list: safeArray(legacy[legacyListField]), summary: legacy[legacySummaryField] || {}, scope: legacy.scope || null, notes: [], excluded: false };
  }
  return { source: null, list: null, summary: {}, scope: null, notes: [], excluded: false };
}

export function selectPlacesLayers(result) {
  if (!result) return null;
  const legacy = result.nearby_places || null;
  const statuses = result.section_status || {};
  const anyNew = PLACES_DATASET_KEYS.some((k) => sent(result[k]));

  const a = pickLayer(result, legacy, 'commercial_anchors', 'anchors', 'anchor_amenities', 'anchor_summary');
  const p = pickLayer(result, legacy, 'public_places', 'places', 'public_places', 'public_places_summary');
  const e = pickLayer(result, legacy, 'adverse_uses', 'facilities', 'externalities', 'externality_summary');

  const hasAny = Boolean(a.list || p.list || e.list || legacy);
  const notes = Array.from(new Set([...a.notes, ...p.notes, ...e.notes, ...(legacy ? safeArray(legacy.notes) : [])]));
  const statusFor = (layer, key) => (sent(result[key]) ? statuses[key] || null : statuses.nearby_places || null);
  const newStatuses = PLACES_DATASET_KEYS.map((k) => statuses[k]).filter(Boolean);
  const status = anyNew
    ? (newStatuses.find((s) => s.status && s.status !== 'ok') || newStatuses[0] || statuses.nearby_places || null)
    : (statuses.nearby_places || null);

  return {
    hasAny,
    mode: anyNew ? 'datasets' : 'nearby_places',
    anchors: a.list,
    anchorSummary: a.summary,
    anchorScope: a.scope,
    publicPlaces: p.list,
    publicSummary: p.summary,
    publicScope: p.scope,
    externalities: e.list,
    externalitySummary: e.summary,
    externalityScope: e.scope,
    families: legacy?.general_amenities || null,
    dataQuality: legacy?.data_quality || null,
    // Legacy scope carries the shared bits (source release, catalogs); split scopes override what they restate.
    scope: { ...(legacy?.scope || {}), ...(e.scope || {}), ...(p.scope || {}), ...(a.scope || {}) },
    notes,
    sources: { anchors: a.source, publicPlaces: p.source, externalities: e.source, families: legacy ? 'nearby_places' : null },
    excluded: { anchors: a.excluded, publicPlaces: p.excluded, externalities: e.excluded },
    statuses: { anchors: statusFor(a, 'commercial_anchors'), publicPlaces: statusFor(p, 'public_places'), externalities: statusFor(e, 'adverse_uses') },
    status,
  };
}
